'use client';
import IconCartActive from '@/assets/icons/cart/shopping-cart-added.svg';
import IconArrowLeft from '@/assets/icons/icon-arrow-left.svg';
import IconArrowRight from '@/assets/icons/icon-arrow-right.svg';
import IconHeart from '@/assets/icons/icon-heart.svg';

import IconPlusAdd from '@/assets/icons/main-page/icon-add-plus.svg';
import ImageProduct1 from '@/assets/images/shopping-cart/image-airpods.png';
import ImageProduct2 from '@/assets/images/shopping-cart/image-smart-watch.png';
import {
  DraftOrderCreateMutation,
  useDefaultChannelQuery,
  useDraftOrderCreateMutation,
  useDraftOrderLineCreateMutation,
  useDraftOrderQuery,
  useUserDraftOrdersQuery,
} from '@/graphql';
import convertToReadable from '@/helpers/convertToReadable';
import { ordersStore } from '@/zustand/orders';
import useUserStore from '@/zustand/useUser';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import Image from 'next/image';
import { useRouter } from '@/i18n/navigation';
import { useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import { Button } from 'rsuite';
import styles from './cards-carousel.module.scss';
import { useGuestStore } from '@/zustand/guestStore';
import { useTranslations } from 'next-intl';
import { decodeBase64Id } from '@/helpers/decodeBase64Id';

const CardsCarousel = ({ combinedProducts }: any) => {
  const splideRef = useRef<any>(null);
  const userData = useUserStore((user: any) => user?.user);
  const isLoggedIn = userData?.id;
  const orderId = useGuestStore(state => state.guestOrderId);

  const router = useRouter();
  const { width } = useWindowSize();

  const { data: draftOrdersData, refetch: refetchDraftOrders } =
    useUserDraftOrdersQuery({
      variables: {
        userId: userData?.id,
      },
    });

  const {
    data: guestDraftOrdersData,
    loading: guestDataLaoding,
    refetch: refetchGuestDraftOrdersData,
  } = useDraftOrderQuery({
    variables: {
      id: orderId || '',
    },
    skip: !orderId,
  });
  const [addProductOrderLineToCart] = useDraftOrderLineCreateMutation({
    context: {
      requiresAuth: true,
    },
    onCompleted: () => {
      refetchDraftOrders();
    },
  });
  ///////////////////////////////////////////////////
  const setOrders = ordersStore(e => e.setOrders);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );
  const [openCartModal, setOpenCartModal] = useState(false);

  const { data: channelData } = useDefaultChannelQuery();
  const handleCartOpen = () => {
    setOpenCartModal(true);
  };

  const handleSuccessOrder = (data: DraftOrderCreateMutation) => {
    if (data?.draftOrderCreate?.errors?.length === 0) {
      setOrders(data?.draftOrderCreate?.order);
      refetchDraftOrders();
    }
  };
  const [addProductToCart] = useDraftOrderCreateMutation({
    context: {
      requiresAuth: true,
    },
    onCompleted: handleSuccessOrder,
  });

  const addToCart = async (id: any) => {
    try {
      setLoadingStates(prev => ({ ...prev, [id]: true }));

      if (isLoggedIn) {
        if (draftOrdersData?.userDraftOrders?.length === 0) {
          await addProductToCart({
            variables: {
              input: {
                channelId: channelData?.channel?.id,
                user: userData?.id,
                lines: [
                  {
                    variantId: id,
                    quantity: 1,
                  },
                ],
              },
            },
          });
        }
        if (draftOrdersData?.userDraftOrders?.[0]?.isFastBuy === false) {
          await addProductOrderLineToCart({
            variables: {
              id: draftOrdersData?.userDraftOrders?.[0]?.id,
              input: [
                {
                  variantId: id,
                  quantity: 1,
                },
              ],
            },
          });
        }
      } else {
        if (!orderId) {
          const orderGuestData = await addProductToCart({
            variables: {
              input: {
                channelId: channelData?.channel?.id,
                lines: [
                  {
                    variantId: id,
                    quantity: 1,
                  },
                ],
              },
            },
          });
          const guestOrderId =
            orderGuestData?.data?.draftOrderCreate?.order?.id;
          typeof guestOrderId === 'string' &&
            useGuestStore.getState().setGuestOrderId(guestOrderId);
        } else {
          await addProductOrderLineToCart({
            variables: {
              id: orderId,
              input: [
                {
                  variantId: id,
                  quantity: 1,
                },
              ],
            },
          });
        }
      }
      isLoggedIn
        ? await refetchDraftOrders()
        : await refetchGuestDraftOrdersData();
      handleCartOpen();
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      // Clear loading state
      setLoadingStates(prev => ({ ...prev, [id]: false }));
    }
  };

  // Function to go to the next slide
  const goToNextSlide = () => {
    const splide = splideRef.current?.splide;
    if (splide) {
      splide.go('>');
    }
  };
  const goToPrevSlide = () => {
    const splide = splideRef.current?.splide;
    if (splide) {
      splide.go('<');
    }
  };

  const isProductInCart = (productId: any) => {
    if (isLoggedIn) {
      return draftOrdersData?.userDraftOrders?.[0]?.lines?.some(
        (line: any) => line?.productVariantId === productId
      );
    }
    return guestDraftOrdersData?.draftOrder?.lines?.some(
      (line: any) => line?.productVariantId === productId
    );
  };
  const t = useTranslations('General');

  return (
    <div className={styles.carousel}>
      <Splide
        ref={splideRef}
        options={{
          rewind: true,
          updateOnMove: true,
          fixedWidth: 'auto',
          perPage: 2,
          perMove: 1,
          arrows: false,
          pagination: false,
          gap: '12px',
          breakpoints: {
            1024: {
              perPage: 3,
              gap: '8px',
            },
            768: {
              perPage: 1,
              gap: '8px',
            },
            480: {
              perPage: 1,
              gap: '8px',
            },
          },
        }}
        hasTrack={false}
        style={{ padding: '0' }}
      >
        <SplideTrack>
          {combinedProducts?.map((product: any) => {
            const displayVariant =
              product?.defaultVariant || product?.variants?.[0];
            return (
              <SplideSlide key={product.id}>
                <div
                  className={`${styles.slide} `}
                  onClick={() =>
                    router.push(
                      `/product/${decodeBase64Id(displayVariant?.id)}-${displayVariant?.product.slug}`
                    )
                  }
                >
                  <div className={styles.img}>
                    <Image
                      src={
                        displayVariant?.media?.[0]?.url || '/default-image.jpg'
                      }
                      width={width < 768 ? 88 : 68}
                      height={width < 768 ? 74 : 68}
                      // style={{
                      //   width: "100%",
                      //   height: "auto",
                      // }}
                      alt={product?.title || 'product'}
                    />
                  </div>
                  <div className={styles.texts}>
                    <h3>{displayVariant?.name || 'No title'}</h3>
                    <div>
                      {displayVariant?.pricing?.installmentPrice12?.amount ? (
                        <div className={styles.monthPrice}>
                          от{' '}
                          {convertToReadable(
                            displayVariant?.pricing?.installmentPrice12?.amount
                          ) || 0}{' '}
                          {t('price-month')}
                        </div>
                      ) : (
                        ''
                      )}

                      <div className={styles.bottom}>
                        <div className={styles.prices}>
                          {displayVariant?.pricing?.priceUndiscounted?.net
                            ?.amount ? (
                            <div className={styles.discount}>
                              {convertToReadable(
                                displayVariant?.pricing?.priceUndiscounted?.net
                                  ?.amount
                              )}{' '}
                              {t('price')}
                            </div>
                          ) : null}
                          <div className={styles.price}>
                            {' '}
                            {convertToReadable(
                              displayVariant?.pricing?.price?.net?.amount || 0
                            )}{' '}
                            {t('price')}
                          </div>
                        </div>
                        <div className={styles.btnGroup}>
                          <Button type="button" className={styles.like}>
                            <IconHeart />
                          </Button>
                          {isProductInCart(displayVariant?.id) ? (
                            <Button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                e.preventDefault();
                                router.push('/cart');
                              }}
                              className={styles.icon}
                            >
                              <IconCartActive />
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                e.preventDefault();
                                addToCart(displayVariant?.id);
                              }}
                              className={styles.icon}
                            >
                              <IconPlusAdd />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </SplideSlide>
            );
          })}
        </SplideTrack>
        {width > 768 && (
          <>
            <button
              type="button"
              className={styles.carouselPrevButton}
              onClick={goToPrevSlide}
            >
              {' '}
              <IconArrowLeft />
            </button>
            <button
              type="button"
              className={styles.carouselNextButton}
              onClick={goToNextSlide}
            >
              {' '}
              <IconArrowRight />
            </button>
          </>
        )}
      </Splide>
    </div>
  );
};

export default CardsCarousel;
